import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Bell,
  CalendarClock,
  Compass,
  FileText,
  Trophy,
  MapPin,
  Loader2,
  ScrollText,
  ListTodo,
  UserCheck,
  Building2,
  Flag,
} from "lucide-react";

import { cn } from "@/lib/utils";
import {
  useNotifications,
  useUnreadCount,
} from "@/hooks/queries/useNotifications";
import { useMarkAllNotificationsRead } from "@/hooks/mutations/useNotificationMutations";
import { capture } from "@/lib/posthog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  translateSubject,
  subjectClass,
} from "@/components/contractings/pills";
import { formatMoney } from "@/lib/format";
import type { AppNotification, MatchedInterest } from "@/types/domain";

/** Data no sensible que el backend adjunta a una notificación NEW_MATCH. */
// Cómo se ve cada tipo de aviso en la campana: mismo color que su sección del
// correo (`BRAND` en emails/layout.ts). Traspasos y tareas son del CRM y no van al correo.
const NOTIFICATION_LOOK: Record<
  string,
  { Icon: typeof Compass; iconColor: string }
> = {
  CONTRACTING_AWARDED: { Icon: Trophy, iconColor: "text-emerald-600" },
  SCHEDULE_CHANGED: { Icon: CalendarClock, iconColor: "text-amber-600" },
  DOCUMENTS_PUBLISHED: { Icon: FileText, iconColor: "text-violet-600" },
  PROCEDURE_ACTION: { Icon: ScrollText, iconColor: "text-teal-700" },
  STAGE_CHANGED: { Icon: Flag, iconColor: "text-blue-700" },
  CONTRACTING_ASSIGNED: { Icon: UserCheck, iconColor: "text-slate-600" },
  TASK_DUE: { Icon: ListTodo, iconColor: "text-red-500" },
  NEW_MATCH: { Icon: Compass, iconColor: "text-blue-600" },
  // Una empresa que sigues ganó: el color de Empresas. No va al correo.
  COMPANY_AWARDED: { Icon: Building2, iconColor: "text-purple-600" },
};

type NewMatchMetadata = {
  contractSubject?: string | null;
  location?: string | null;
  /** Solo cuando el aviso no es de convocatoria ("CONTRATADO", "DESIERTO"). */
  stage?: string | null;
  currencySymbol?: string | null;
  matchedInterests?: MatchedInterest[];
};

function readNewMatchMetadata(
  metadata: Record<string, unknown> | null,
): NewMatchMetadata | null {
  if (!metadata) return null;
  const matches = metadata.matchedInterests;
  if (!Array.isArray(matches) || matches.length === 0) return null;
  return metadata as NewMatchMetadata;
}

/** Tiempo relativo corto en español (ej. "hace 5 min", "hace 2 d"). */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

/**
 * Detalle de una oportunidad matcheada, con la misma estética que la tabla del
 * dashboard: objeto (bien/servicio/obra), ubicación y chips de coincidencia con
 * su monto. Solo data que el gating deja visible en licitaciones bloqueadas.
 */
function MatchDetails({ meta }: { meta: NewMatchMetadata }) {
  const symbol = meta.currencySymbol || "S/";
  return (
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {meta.contractSubject && (
          <span
            className={cn(
              "rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
              subjectClass(meta.contractSubject),
            )}
          >
            {translateSubject(meta.contractSubject)}
          </span>
        )}
        {meta.location && (
          <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {meta.location}
          </span>
        )}
        {meta.stage && (
          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {meta.stage.replaceAll("_", " ")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {(meta.matchedInterests ?? []).map((m, i) => {
          const isItem = m.kind === "item";
          return (
            <span
              key={`${m.description}-${i}`}
              title={m.description}
              className={cn(
                "inline-flex max-w-[300px] items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[10px] font-semibold",
                isItem
                  ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                  : "border-primary/20 bg-primary/5 text-primary",
              )}
            >
              <span
                className={cn(
                  "size-1 shrink-0 rounded-full",
                  isItem ? "bg-emerald-500" : "bg-primary",
                )}
              />
              <span className="truncate">{m.description}</span>
              {m.amount > 0 && (
                <span className="shrink-0 font-mono font-extrabold">
                  {formatMoney(m.amount, symbol)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function NotificationRow({
  notification,
  unread,
  onClick,
}: {
  notification: AppNotification;
  unread: boolean;
  onClick: (n: AppNotification) => void;
}) {
  const { Icon, iconColor } = NOTIFICATION_LOOK[
    notification.notification_type
  ] ?? { Icon: Compass, iconColor: "text-blue-600" };
  const match =
    notification.notification_type === "NEW_MATCH"
      ? readNewMatchMetadata(notification.notification_metadata)
      : null;

  return (
    <button
      onClick={() => onClick(notification)}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent",
        unread && "bg-primary/5",
      )}
    >
      <div className={cn("mt-0.5 shrink-0", iconColor)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug">
          {notification.notification_title}
        </p>
        {match ? (
          <MatchDetails meta={match} />
        ) : (
          notification.notification_body && (
            <p className="line-clamp-1 text-xs text-muted-foreground">
              {notification.notification_body}
            </p>
          )
        )}
        <span className="text-[11px] text-muted-foreground">
          {relativeTime(notification.notification_createdat)}
        </span>
      </div>
      {unread && (
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
      )}
    </button>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // IDs no leídos al momento de abrir: mantienen el resaltado azul durante la
  // apertura aunque el marcado automático ya limpió el badge. `null` = aún sin snapshot.
  const [seenUnreadIds, setSeenUnreadIds] = useState<Set<number> | null>(null);
  const { data: unread = 0 } = useUnreadCount();
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications(open);
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.pages.flatMap((p) => p.items) ?? [];

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Al abrir, congela las no leídas y márcalas todas como vistas (una sola vez).
  useEffect(() => {
    if (open) capture("notifications_opened", { unread });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSeenUnreadIds(null);
      return;
    }
    if (seenUnreadIds !== null || notifications.length === 0) return;
    const unreadIds = notifications
      .filter((n) => !n.notification_readat)
      .map((n) => n.notification_id);
    setSeenUnreadIds(new Set(unreadIds));
    if (unreadIds.length > 0) markAllRead.mutate();
  }, [open, notifications, seenUnreadIds, markAllRead]);

  // Infinite scroll: carga la siguiente página cuando el sentinel entra al viewport.
  useEffect(() => {
    const root = viewportRef.current;
    const target = sentinelRef.current;
    if (!open || !root || !target || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: "120px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    open,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    notifications.length,
  ]);

  function handleClick(n: AppNotification) {
    setOpen(false);
    if (n.contracting_id) navigate(`/contracting/${n.contracting_id}`);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative flex h-10 w-10 items-center justify-center rounded-full border border-slate-100 bg-card text-slate-500 shadow-sm transition-colors hover:text-slate-800 dark:border-slate-800 dark:bg-slate-900 dark:hover:text-slate-200">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-red-500 px-1 text-[10px] font-bold text-white dark:border-slate-900">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-bold">Notificaciones</span>
        </div>
        <ScrollArea
          viewportRef={viewportRef}
          className="h-[360px] overflow-hidden"
        >
          {isLoading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Cargando...
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <Bell className="h-7 w-7 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">
                No tienes notificaciones.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.notification_id}
                  notification={n}
                  unread={
                    seenUnreadIds
                      ? seenUnreadIds.has(n.notification_id)
                      : !n.notification_readat
                  }
                  onClick={handleClick}
                />
              ))}
              <div ref={sentinelRef} />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-3 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
